import { MESH_ID_SEPARATOR } from '../meshConstants'

import { ModeDefinition, Structure } from '@wix/thunderbolt-becky-types'
import { DataForMeshCalculation, MeshComponent, OverridesData } from '../mesh'
import { MeshResult } from '../../../types/css'
import _ from 'lodash'
import { getMeshComponents, getMeshData, getStylesAndWedgesAndRotated } from '../meshUtils'
import { MeshStyles } from 'santa-mesh/cjs/src/Types' // eslint-disable-line no-restricted-syntax

const getRotatedComponentsIds = (components: Array<MeshComponent>): Array<string> =>
	components.filter((comp) => comp.rotationInDegrees).map((comp) => comp.id)

// If component is rotated only in one mode, we should add fake rotation in the other mode in order to get the right style
const addFakeRotated = (
	meshComponents: Array<MeshComponent>,
	rotatedComponentsIds: Array<string>
): Array<MeshComponent> =>
	meshComponents.map((comp) =>
		rotatedComponentsIds.includes(comp.id) ? { ...comp, rotationInDegrees: comp.rotationInDegrees || 360 } : comp
	)

export const getMeshResultForHoverBox = (
	hoverBoxCompId: string,
	compMeshData: DataForMeshCalculation,
	overridesData: OverridesData
): MeshResult => {
	const { compStructure: fullCompStructure, compData: data, resolvedModes: resolvedStructure } = compMeshData
	const definitions = _.get(fullCompStructure, 'modes.definitions', []) as Array<ModeDefinition>

	const getResolvedStructureByDefinitionType = (type: 'HOVER' | 'DEFAULT') => {
		const definitionId = definitions.find((def) => def.type === type)!.modeId
		const components = resolvedStructure![definitionId][hoverBoxCompId].components!.concat(hoverBoxCompId)
		return _.pickBy(resolvedStructure![definitionId], (val, componentId) => components.includes(componentId))
	}

	const getMeshDataForTransition = (
		toStructure: Structure,
		fromStructure: Structure,
		hoverBoxChildren: Array<string>,
		rotatedComponents: Array<string>
	) => {
		const hoverBoxComponent = {
			[hoverBoxCompId]: { ...fromStructure[hoverBoxCompId], components: hoverBoxChildren },
		}

		const mergedStructure = { ...{ ...toStructure, ...fromStructure }, ...hoverBoxComponent }
		const childComponents = getMeshComponents(hoverBoxCompId, mergedStructure)
		return getMeshData(
			{
				compStructure: mergedStructure[hoverBoxCompId],
				compData: data,
			},
			addFakeRotated(childComponents, rotatedComponents),
			overridesData
		)
	}

	const resolvedHoverStructure = getResolvedStructureByDefinitionType('HOVER') as Structure
	const resolvedDefaultStructure = getResolvedStructureByDefinitionType('DEFAULT') as Structure

	const hoverBoxChildren = fullCompStructure.components || []

	// HoverBox component for each modes
	const defaultModeComponents = getMeshComponents(hoverBoxCompId, resolvedDefaultStructure)
	const hoverModeComponents = getMeshComponents(hoverBoxCompId, resolvedHoverStructure)

	const rotatedComponents = _.uniq(
		getRotatedComponentsIds(defaultModeComponents).concat(getRotatedComponentsIds(hoverModeComponents))
	)

	const defaultModeMeshData = getMeshData(
		{
			compStructure: resolvedDefaultStructure[hoverBoxCompId],
			compData: data,
		},
		addFakeRotated(defaultModeComponents, rotatedComponents),
		overridesData
	)

	// Mesh result for hover mode
	const hoverMeshData = getMeshData(
		{
			compStructure: resolvedHoverStructure[hoverBoxCompId],
			compData: data,
		},
		addFakeRotated(hoverModeComponents, rotatedComponents),
		overridesData
	)
	const hoverMeshResult = getStylesAndWedgesAndRotated(hoverBoxCompId, hoverMeshData)

	// Mesh data for hover-in
	const hoverInMeshData = getMeshDataForTransition(
		resolvedDefaultStructure,
		resolvedHoverStructure,
		hoverBoxChildren,
		rotatedComponents
	)

	// Mesh data for hover-out
	const hoverOutMeshData = getMeshDataForTransition(
		resolvedHoverStructure,
		resolvedDefaultStructure,
		hoverBoxChildren,
		rotatedComponents
	)

	const defaultMeshStyles = _.mapValues(defaultModeMeshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}default`)
	const hoverMeshStyles = _.mapValues(hoverMeshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}hover`)
	const hoverInMeshStyles = _.mapValues(hoverInMeshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}hoverIn`)
	const hoverOutMeshStyles = _.mapValues(hoverOutMeshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}hoverOut`)

	const combinedStyles = { ...defaultMeshStyles, ...hoverMeshStyles, ...hoverInMeshStyles, ...hoverOutMeshStyles }

	const defaultMeshResult = getStylesAndWedgesAndRotated(hoverBoxCompId, {
		...defaultModeMeshData,
		styles: combinedStyles as MeshStyles,
	})

	const allWedges = _.uniq(defaultMeshResult.wedges.concat(hoverMeshResult.wedges))
	const allRotated = _.uniq(defaultMeshResult.rotatedComponents.concat(hoverMeshResult.rotatedComponents))

	return {
		...defaultMeshResult,
		rotatedComponents: allRotated,
		wedges: allWedges,
	}
}
